import * as React from 'react';

import * as Yup from 'yup';
import {
	Field, FieldProps,
} from 'formik';
import Modal from 'antd/es/modal';

import { ChatKind } from '@common/react/components/Chat/Chat';
import { BaseParams } from '@common/react/objects/BaseParams';
import { getUserName } from '@common/react/utils/utils';
import { BaseUserWithAvatar } from '@common/react/objects/BaseUser';
import { FormikInput } from '@common/react/components/Forms/FormikInput/FormikInput';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { FormikFieldUserList } from '@common/react/components/Chat/AddChatPlugin/FormikFieldUserList';

import { Location } from '@app/objects/Location';
import { getDashboardContainer } from '@app/components/Utils';
import LocationSelect from '@app/components/UI/LocationSelect/LocationSelect';

const validationSchema = Yup.object().shape({
	kind: Yup.number(),
	contacts: Yup.array().when('kind', {
		is: ChatKind.Group,
		then: Yup.array().min(1, 'You must select users'),
	}),
});

interface AddChatPopupProps {
	user: BaseUserWithAvatar;
	render: (show: () => void) => void;
	autocompleteParams?: BaseParams;
}

interface FormValues {
	id: number;
	locationId: number | null;
	location: Location | null;
	name: string;
	kind: ChatKind;
	contacts: Array<BaseUserWithAvatar>;
}

const AddSupportChatPopup: React.FC<AddChatPopupProps> = (props) => {
	const [popupShow, setPopupShow] = React.useState(false);

	const popupHide = () => setPopupShow(false);

	const { user: loggedUser, autocompleteParams } = props;

	const item = {
		id: -1,
		locationId: null,
		location: null,
		name: '',
		kind: ChatKind.Group,
		contacts: [],
	};

	return (
		<>
			{props.render(() => setPopupShow(true))}
			<Modal
				title="Edit Chat"
				open={popupShow}
				onCancel={popupHide}
				destroyOnHidden
				getContainer={getDashboardContainer}
				width={500}
				footer={null}
			>
				<ItemProvider<FormValues>
					id={-1}
					loadRequest="SupportChat"
					saveRequest="SupportChat"
					add={item}
					clearForSubmit={({ item }) => ({
						locationId: item.locationId,
						userId: item.contacts[0].id,
					})}
					resetAfterSave
					validationSchema={validationSchema}
				>
					<ItemEditor<FormValues>
						formikProps={{
							enableReinitialize: true,
						}}
						withButtons
						afterSubmit={() => setPopupShow(false)}
						edit={({ formikBag }) => <>
							<div className="row">
								<Field name="locationId">
									{(fieldProps: FieldProps<FormValues>) => (
										<FormikInput
											fieldProps={fieldProps}
											title="Location"
											render={({ field, form }: FieldProps<FormValues>) => (
												<LocationSelect
													asSelect={false}
													onChange={(e, location) => {
														const value = e.currentTarget.value;
														form.setFieldValue(field.name, +value);
														form.setFieldValue('location', location);
													}}
													autocompleteProps={{
														value: formikBag.values.location ? formikBag.values.location.nameEn : '',
														antdProps: {
															id: 'locationId',
														},
													}}
													location={formikBag.values.location}
													withEye
												/>
											)}
										/>
									)}
								</Field>
							</div>
							<div className="row">
								<FormikFieldUserList<BaseUserWithAvatar, FormValues>
									fieldName="contacts"
									label="Contacts*"
									className={`form-group col-sm-12 ${formikBag.errors.contacts && formikBag.touched.contacts ? 'has-error' : ''}`}
									renderItem={getUserName}
									requestType="patientViewList"
									filterComponentProps={{
										antdProps: {
											showSearch: {
												filterOption: (inputValue, option: any) => {
													const id = +(option.key as string);
													return loggedUser && loggedUser.id !== id
														&& !formikBag.values
															.contacts.some((item: BaseUserWithAvatar) => id === item.id);
												},
											},
										},
										params: autocompleteParams,
									}}
								/>
							</div>
						</>}
					/>
				</ItemProvider>
			</Modal>
		</>
	);
};

export default AddSupportChatPopup;
